import { entityStore, Measurements } from '~/utility/BinSentry-ui-utility';
import moment from 'moment';
import { html, LitEntity, SirenActionMixin } from '../../utility/lit-entity';
import '../../bins/parts/bin-levels';
import './device-events';
import { PAYLOAD_TYPE } from '../props/device-event-payload-type';

class BinMonitoringChartData extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.totalVolumes = {};
  }

  static get properties() {
    return {
      eventsHref: { type: String, attribute: false },
      totalVolumes: { type: Object, attribute: false },
      start: { type: String },
      end: { type: String },
    };
  }

  render() {
    return html`
      <bin-levels
        .href="${this.levelsHref}"
        .start="${this.start}"
        .end="${this.end}"
        @update="${this._onUpdateLevels}"
      ></bin-levels>
      <device-events
        .href="${this.eventsHref}"
        .start="${this.start}"
        .end="${this.end}"
        @update="${this._onUpdateEvents}"
      ></device-events>
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.levelsHref = this.getHref('https://api.binsentry.com/rel/bin-levels');
    this.eventsHref = this.getHref('https://api.binsentry.com/rel/events');
  }

  async _updateTotalVolumes(levels) {
    const measurement = Measurements.get('volume');
    for (const level of levels) {
      const binHref = (level.getSubEntityByRel('https://api.binsentry.com/rel/bin') || {}).href;
      if (!binHref || this.totalVolumes[binHref] !== undefined) {
        continue;
      }

      try {
        const { entity } = await entityStore.fetchWait(binHref);
        // Intentionally using volumes.total as the graph should not look bad when a bin has filled into the topper
        this.totalVolumes[binHref] = measurement.convert({ volume: this.getProperty('volumes.total', 0, entity) });
      } catch (e) {
        // ignore the error. This happens when bin is not found, so still show the graph, just don't calculate the total volume
      }
    }
  }

  async _onUpdateLevels(e) {
    const { entity } = e.detail;

    await this._updateTotalVolumes((entity && entity.getSubEntitiesByClass('level')) || []);
    this.levelsStartDate = moment(entity.properties.start);
    const measurement = Measurements.get('volume');

    this.levels = entity.entities.map((level) => {
      const { publishedAt, volume } = level.properties;
      const binId = level.getSubEntityByRel('https://api.binsentry.com/rel/bin').href;
      const value = measurement.convert({ volume, totalVolume: this.totalVolumes[binId] });
      const date = moment(publishedAt);

      const device = level.getSubEntityByClass('device');
      if (device && device.href !== this.href) {
        return;
      }

      return {
        ...level.properties,
        binId,
        date,
        rawDate: publishedAt,
        unixEpoch: date.valueOf(),
        value,
        rawVolume: volume.toFixed(2),
      };
    }).filter((level) => level);

    this._dataUpdated();
  }

  async _onUpdateEvents(e) {
    const { entity } = e.detail;
    if (!entity) {
      return;
    }

    this.eventsStartDate = moment(entity.properties.startDate);

    let nextValidSetupISODate = '';
    const filteredEvents = entity.getSubEntitiesByClass('event').filter(event => {
      // Keep all non SETUP events
      if (event.properties.payloadType !== PAYLOAD_TYPE.SETUP) {
        nextValidSetupISODate = '';
        return true;
      }

      // Only show 1 SETUP event per hour of data
      if (!nextValidSetupISODate || event.properties.date < nextValidSetupISODate) {
        nextValidSetupISODate = moment(event.properties.date).subtract(1, 'hour').toISOString();
        return true;
      }
      return false;
    });

    this.events = filteredEvents.map((event) => {
      const date = moment(event.properties.date);
      return {
        ...event.properties,
        date,
        rawDate: event.properties.date,
        unixEpoch: date.valueOf(),
      };
    });

    this._dataUpdated();
  }

  _dataUpdated() {
    if (!this.levelsStartDate || !this.eventsStartDate || this.levelsStartDate.diff(this.eventsStartDate, 'minutes') > 1) {
      return;
    }

    this.dispatchEvent(new CustomEvent('chart_data', {
      detail: {
        levels: this.levels,
        events: this.events,
        totalVolumes: this.totalVolumes,
      },
      composed: true,
      bubbles: true,
    }));
  }
}

window.customElements.define('device-monitoring-chart-data', BinMonitoringChartData);
